import { useTranslation } from 'react-i18next';
import { Copy, Pencil, Trash2, Layers, Link2 } from 'lucide-react';
import { CURRENCIES } from '@/lib/calculationEngine';

const symbolOf = (config) => CURRENCIES.find(c => c.value === config?.currency)?.symbol || '€';

/** One scenario set: the baseline estimate plus its alternatives. */
export function ScenarioSetCard({ set, selectedIds, onToggle, onDuplicate, onRename, onDelete, canManage, shareCount = 0, children }) {
  const { t } = useTranslation();

  return (
    <div className="border border-[#DDE1E5] rounded-lg bg-white" data-testid={`scenario-set-${set.group_id}`}>
      <div className="px-5 py-3.5 border-b border-[#DDE1E5] flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0 flex items-center gap-2.5">
          <Layers className="w-4 h-4 text-[#66707A] shrink-0" aria-hidden="true" />
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.12em] text-[#8A929B]"
                 style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
              {t('scenarios.setLabel')}
            </div>
            <h3 className="text-sm font-medium text-[#171A1D] truncate">{set.name}</h3>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {shareCount > 0 && (
            <span
              className="inline-flex items-center gap-1 border border-[#527F19]/35 bg-[#F1F8E8] px-1.5 py-0.5 rounded-sm text-[10px] font-medium uppercase tracking-[0.08em] text-[#527F19]"
              style={{ fontFamily: 'IBM Plex Mono, monospace' }}
              data-testid={`scenario-set-shares-${set.group_id}`}
            >
              <Link2 className="w-3 h-3" aria-hidden="true" /> {shareCount}
            </span>
          )}
          <span className="text-xs text-[#66707A]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
            {t('scenarios.count', { count: set.scenario_count })}
          </span>
        </div>
      </div>

      <ul x-excluded="true" className="divide-y divide-[#DDE1E5]">
        {set.estimates.map(estimate => {
          const selected = selectedIds.includes(estimate.estimate_id);
          const symbol = symbolOf(estimate.config);
          return (
            <li
              key={estimate.estimate_id}
              className={`px-5 py-3 flex flex-wrap items-center gap-3 transition-colors duration-150 ${selected ? 'bg-[#F1F8E8]' : ''}`}
              data-testid={`scenario-item-${estimate.estimate_id}`}
            >
              <label className="flex items-center gap-2.5 min-w-0 flex-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => onToggle(estimate.estimate_id)}
                  className="w-4 h-4 accent-[#527F19] shrink-0"
                  data-testid={`scenario-select-${estimate.estimate_id}`}
                  aria-label={`${t('scenarios.select')}: ${estimate.name}`}
                />
                <span className="min-w-0">
                  <span className="block text-sm text-[#171A1D] truncate">{estimate.name}</span>
                  <span className="block text-xs text-[#66707A]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                    {symbol}{Number(estimate.results?.totalCost || 0).toLocaleString()}
                    {' · '}{estimate.results?.totalEffort || 0}d
                    {' · '}{estimate.is_baseline ? t('scenarios.baseline') : t('scenarios.alternative')}
                  </span>
                </span>
              </label>

              {canManage && (
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => onDuplicate(estimate)}
                    className="inline-flex items-center justify-center w-8 h-8 rounded-[6px] border border-[#DDE1E5] text-[#66707A] hover:text-[#171A1D] hover:bg-[#F5F6F7] transition-colors duration-150"
                    data-testid={`scenario-duplicate-${estimate.estimate_id}`}
                    aria-label={t('scenarios.duplicate')}
                    title={t('scenarios.duplicate')}
                  >
                    <Copy className="w-3.5 h-3.5" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={() => onRename(estimate)}
                    className="inline-flex items-center justify-center w-8 h-8 rounded-[6px] border border-[#DDE1E5] text-[#66707A] hover:text-[#171A1D] hover:bg-[#F5F6F7] transition-colors duration-150"
                    data-testid={`scenario-rename-${estimate.estimate_id}`}
                    aria-label={t('scenarios.rename')}
                    title={t('scenarios.rename')}
                  >
                    <Pencil className="w-3.5 h-3.5" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(estimate)}
                    className="inline-flex items-center justify-center w-8 h-8 rounded-[6px] border border-[#DDE1E5] text-[#66707A] hover:text-[#B42318] hover:bg-[#B42318]/[0.06] transition-colors duration-150"
                    data-testid={`scenario-delete-${estimate.estimate_id}`}
                    aria-label={t('scenarios.delete')}
                    title={t('scenarios.delete')}
                  >
                    <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {children}
    </div>
  );
}
